import { ITourOrder, TourEvent, TourRoute } from "training/types";
import { ITrainingProgress } from "types/Training";

/**
 * CLE-3015 — the in-memory state the tour carries across a route hand-off.
 *
 * A tour host lives inside a page (the hospital view, the create page), so it unmounts when the route
 * changes. Before it does, the engine writes here which step comes next and the progress as it stands
 * (the debounced PUT may not have reached the server, and the cached GET is older still); the host on
 * the next page reads it and carries on. Module-level on purpose: it must outlive both hosts, and it is
 * UI state that never needs to reach Redux devtools or the server.
 *
 * Nothing here survives a reload — that is what the saved progress and the banner's Resume are for.
 */
export interface ITourHandoff {
  accountId: string;
  route: TourRoute;
  stepId: string;
  progress: ITrainingProgress;
}

interface ITourSession {
  /** A driver instance is up (between start and exit / hand-off / finish). */
  running: boolean;
  route: TourRoute | null;
  /**
   * Set by the engine just before a route change; picked up by the host on the next page, and cleared once
   * that page's first step has been highlighted — so a host that is unmounted and mounted again before
   * then (a page that re-mounts it) still finds it.
   */
  handoff: ITourHandoff | null;
  /** The latest progress the engine wrote, newer than anything the server has returned. */
  progress: ITrainingProgress | null;
  /** The trainee's estimate, from the create page's `order-created` event. */
  order: ITourOrder | null;
  /** Bumped by the banner's Start / Resume; the host on the page starts the tour when it changes. */
  startRequests: number;
}

const initialState = (): ITourSession => ({
  running: false,
  route: null,
  handoff: null,
  progress: null,
  order: null,
  startRequests: 0,
});

let state: ITourSession = initialState();
const listeners = new Set<() => void>();

export function getTourSession(): Readonly<ITourSession> {
  return state;
}

export function updateTourSession(patch: Partial<ITourSession>) {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
}

export function subscribeTourSession(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Tests only. */
export function resetTourSession() {
  state = initialState();
  listeners.clear();
}

/** The banner's Start / Resume, wired where a host is already mounted. */
export function requestTrainingTourStart() {
  updateTourSession({ startRequests: state.startRequests + 1 });
}

/**
 * True while the tour is running on the create page. Submit reads it to send `training: true` with
 * Create and Send, which tags the estimate in its history (OrderHistory TrainingEstimateCreated).
 */
export function isTrainingTourOnCreatePage() {
  return state.running && state.route === "create";
}

/** The window event a page dispatches for an `advanceOn: "event"` step. */
export const tourEventName = (event: TourEvent) => `clearcertified:${event}`;

/** Called by the create page after Create and Send succeeded while the tour runs. Never throws. */
export function notifyTrainingOrderCreated(order: ITourOrder) {
  try {
    window.dispatchEvent(
      new CustomEvent(tourEventName("order-created"), { detail: order })
    );
  } catch (error) {
    // The estimate exists either way; the tour simply does not move on.
    console.error("[Clear Certified] could not notify the tour", error);
  }
}
